import { useState, useEffect } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { Wrench, Lock } from 'lucide-react';
import { publicData } from '@/lib/publicData';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LOGO = 'https://customer-assets.emergentagent.com/job_ogmini-next/artifacts/5o4gglyz_TM%20%282%29.png';

/**
 * Wraps the public storefront. If the admin has flipped the site to OFFLINE
 * (site_settings.site_live = false), customers see a polished maintenance
 * screen instead. Admin / POS / login routes are always allowed through so
 * staff can still flip the switch back.
 */
const ALLOWED_PATHS = [
  '/admin', '/admin-portal', '/pos', '/admin/login', '/pos/login',
  '/login', '/policies/privacy', '/policies/terms', '/policies/shipping-refund',
];

function isAllowed(pathname) {
  return ALLOWED_PATHS.some((p) => pathname === p || pathname.startsWith(`${p}/`));
}

export default function SiteLiveGate({ children }) {
  const [state, setState] = useState({ loading: true, live: true, message: '' });
  const { pathname } = useLocation();

  useEffect(() => {
    let cancelled = false;
    publicData('settings')
      .then((data) => {
        if (cancelled) return;
        setState({
          loading: false,
          live: data?.site_live !== false, // missing field defaults to live
          message: data?.site_offline_message || '',
        });
      })
      .catch(() => { if (!cancelled) setState({ loading: false, live: true, message: '' }); });
    return () => { cancelled = true; };
  }, []);

  if (state.loading) return null; // brief — avoid flashing maintenance page
  if (state.live || isAllowed(pathname)) return children;

  return (
    <div data-testid="site-offline" className="min-h-screen bg-[#0a0a0a] text-white flex flex-col items-center justify-center px-6 text-center relative overflow-hidden">
      {/* Subtle grid background */}
      <div className="absolute inset-0 opacity-10" style={{
        backgroundImage: 'linear-gradient(rgba(255,42,42,0.4) 1px, transparent 1px), linear-gradient(90deg, rgba(255,42,42,0.4) 1px, transparent 1px)',
        backgroundSize: '64px 64px',
      }} />
      <img src={LOGO} alt="OGMini" className="h-14 mb-10 relative z-10" style={{ filter: 'invert(1)' }} />
      <div className="relative z-10 max-w-xl">
        <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-[#FF2A2A]/20 border border-[#FF2A2A]/40 mb-6">
          <Wrench size={28} className="text-[#FF2A2A]" />
        </div>
        <h1 className="text-4xl sm:text-5xl font-black tracking-tight mb-4" style={{ fontFamily: '"Bebas Neue", sans-serif' }}>
          We&apos;ll be right back.
        </h1>
        <p className="text-base sm:text-lg text-white/70 leading-relaxed mb-8 whitespace-pre-line">
          {state.message || "The store is briefly offline for updates — please check again in a few minutes."}
        </p>
        <p className="text-[11px] tracking-[0.3em] uppercase text-white/40 inline-flex items-center gap-2">
          <Lock size={11} /> Maintenance mode
        </p>
      </div>
    </div>
  );
}
